import { Landmark } from 'lucide-react'
import { AccountCard } from './AccountCard'
import type { FinancialConnection } from '@/types'

interface AccountsListProps {
  connections: FinancialConnection[]
  isLoading?: boolean
}

function AccountSkeleton() {
  return (
    <div className="flex animate-pulse items-center gap-3 px-4 py-3">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-gray-100" />
      <div className="flex-1">
        <div className="h-4 w-40 rounded bg-gray-100" />
        <div className="mt-1.5 h-3 w-28 rounded bg-gray-100" />
      </div>
      <div className="h-4 w-20 rounded bg-gray-100" />
    </div>
  )
}

/**
 * The "Accounts" hearth-card: one row per connected account, balances
 * right-aligned in rounded numerals.
 */
export function AccountsList({ connections, isLoading }: AccountsListProps) {
  if (isLoading) {
    return (
      <div className="hearth-card divide-y divide-gray-100">
        <AccountSkeleton />
        <AccountSkeleton />
      </div>
    )
  }

  const rows = connections.flatMap((connection) =>
    connection.accounts.map((account) => ({
      account,
      institutionName: connection.institution_name,
    }))
  )

  if (rows.length === 0) {
    return (
      <div className="hearth-card flex flex-col items-center justify-center px-6 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-gray-400/10 text-gray-400">
          <Landmark className="h-6 w-6" />
        </span>
        <h3 className="mt-4 text-base font-semibold text-gray-900">No accounts yet</h3>
        <p className="mt-1 max-w-sm text-sm text-gray-500">
          Accounts will appear here once a bank is connected.
        </p>
      </div>
    )
  }

  return (
    <div className="hearth-card divide-y divide-gray-100">
      {rows.map(({ account, institutionName }) => (
        <AccountCard key={account.id} account={account} institutionName={institutionName} />
      ))}
    </div>
  )
}
